/**
 * Safe wrapper for localStorage that handles various edge cases:
 * - Server-side rendering (SSR) environments where window is undefined
 * - Browsers with disabled localStorage (privacy mode, permissions)
 * - Mobile WebViews with inconsistent localStorage behavior (esp. Android)
 * - Storage quota exceeded scenarios
 */

// Helper functions to reduce repetition
const isStorageAvailable = (type: 'localStorage' | 'sessionStorage'): boolean => {
  return typeof window !== 'undefined' && window[type] !== null && window[type] !== undefined
}

const safeStorageOperation = <T>(operation: () => T, errorPrefix: string, fallbackValue: T): T => {
  try {
    return operation()
  } catch (error) {
    console.warn(`${errorPrefix} error:`, error)
    return fallbackValue
  }
}

export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    return safeStorageOperation(
      () => (isStorageAvailable('localStorage') ? window.localStorage.getItem(key) : null),
      'localStorage.getItem',
      null
    )
  },

  setItem: (key: string, value: string): void => {
    safeStorageOperation(
      () => {
        if (isStorageAvailable('localStorage')) window.localStorage.setItem(key, value)
      },
      'localStorage.setItem',
      undefined
    )
  },

  removeItem: (key: string): void => {
    safeStorageOperation(
      () => {
        if (isStorageAvailable('localStorage')) {
          window.localStorage.removeItem(key)
        }
      },
      'localStorage.removeItem',
      undefined
    )
  },

  clear: (): void => {
    safeStorageOperation(
      () => {
        if (isStorageAvailable('localStorage')) {
          window.localStorage.clear()
        }
      },
      'localStorage.clear',
      undefined
    )
  },

  key: (index: number): string | null => {
    return safeStorageOperation(
      () => (isStorageAvailable('localStorage') ? window.localStorage.key(index) : null),
      'localStorage.key',
      null
    )
  },

  get length(): number {
    return safeStorageOperation(() => (isStorageAvailable('localStorage') ? window.localStorage.length : 0), 'localStorage.length', 0)
  },
}

export const safeSessionStorage = {
  getItem: (key: string): string | null => {
    return safeStorageOperation(
      () => (isStorageAvailable('sessionStorage') ? window.sessionStorage.getItem(key) : null),
      'sessionStorage.getItem',
      null
    )
  },

  setItem: (key: string, value: string): void => {
    safeStorageOperation(
      () => {
        if (isStorageAvailable('sessionStorage')) {
          window.sessionStorage.setItem(key, value)
        }
      },
      'sessionStorage.setItem',
      undefined
    )
  },

  removeItem: (key: string): void => {
    safeStorageOperation(
      () => {
        if (isStorageAvailable('sessionStorage')) {
          window.sessionStorage.removeItem(key)
        }
      },
      'sessionStorage.removeItem',
      undefined
    )
  },

  clear: (): void => {
    safeStorageOperation(
      () => {
        if (isStorageAvailable('sessionStorage')) {
          window.sessionStorage.clear()
        }
      },
      'sessionStorage.clear',
      undefined
    )
  },

  key: (index: number): string | null => {
    return safeStorageOperation(
      () => (isStorageAvailable('sessionStorage') ? window.sessionStorage.key(index) : null),
      'sessionStorage.key',
      null
    )
  },

  get length(): number {
    return safeStorageOperation(() => (isStorageAvailable('sessionStorage') ? window.sessionStorage.length : 0), 'sessionStorage.length', 0)
  },
}
